import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField, Tooltip } from '@mui/material'
import React from 'react'
import GrantContent from '../../../components/GrantContent'
import type { Status } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import * as Permisos from '../../../utils/Permisos'
import { STATUS_OPTIONS } from '../common/insurer-common'
import { useInsurerFormModel } from './insurer-form-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }
const dialogBtnClass = 'inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40'

const InsurerForm = () => {
    const model = useInsurerFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar aseguradora</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la aseguradora {form.aseguradora}? Esta acción es irreversible.</p>
                </DialogContent>
                <DialogActions className="gap-1 px-6 pb-5">
                    <button type="button" className={`${dialogBtnClass} border border-k-line bg-k-surface text-k-ink hover:bg-k-hover`} onClick={model.cancelDelete}>Cancelar</button>
                    <button type="button" className={`${dialogBtnClass} border-0 bg-k-danger font-bold !text-white hover:brightness-110`} onClick={model.deleteInsurer} disabled={model.deleting}>Eliminar</button>
                </DialogActions>
            </Dialog>

            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description="Da de alta las aseguradoras que no están en el catálogo predefinido."
                    backTo={model.backTo}
                    backLabel="Aseguradoras"
                    actions={model.isEdit && !model.standard ? (
                        <GrantContent permisos={[Permisos.ASEGURADORAS_D]}>
                            <button
                                type="button"
                                className="inline-flex h-10 items-center gap-2 rounded-xl border-0 bg-k-danger px-4 text-sm font-bold !text-white hover:brightness-110 disabled:opacity-40"
                                onClick={model.askDelete}
                                disabled={model.deleting}
                            >
                                <DeleteIcon style={{ fontSize: 18 }} />
                                Eliminar
                            </button>
                        </GrantContent>
                    ) : !model.isEdit ? (
                        <span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>
                    ) : null}
                />

                {model.standard && (
                    <div className="flex items-center gap-3 rounded-[14px] border border-k-line bg-k-sunken px-4 py-3.5 text-sm text-k-ink-2">
                        <LockOutlinedIcon className="text-k-muted" style={{ fontSize: 20 }} />
                        <p className="m-0">Esta aseguradora es parte del catálogo predefinido y no se puede editar ni eliminar.</p>
                    </div>
                )}

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Datos de la aseguradora</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField
                            className="v2-field md:col-span-3" variant="filled" fullWidth required
                            label="Nombre"
                            disabled={model.standard}
                            value={form.aseguradora}
                            onChange={(event) => change.aseguradora(event.target.value)}
                        />
                        <TextField
                            className="v2-field" variant="filled" fullWidth required
                            label="Abreviación"
                            disabled={model.standard}
                            helperText="Máximo 10 caracteres."
                            value={form.abreviacion}
                            onChange={(event) => change.abreviacion(event.target.value)}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Nombre corto con el que se conoce a la aseguradora">
                                                <HelpOutlineIcon className="text-k-muted-2" style={{ fontSize: 18 }} />
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select required
                            label="Estatus"
                            disabled={model.standard}
                            value={form.status}
                            onChange={(event) => change.status(event.target.value as Status)}
                            slotProps={{ select: selectMenu }}
                        >
                            {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <span className="inline-flex items-center gap-2">
                                        <span className={`h-2.5 w-2.5 rounded-full ${option.dot}`} />
                                        {option.label}
                                    </span>
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        {model.standard ? 'Volver' : 'Cancelar'}
                    </button>
                    {!model.standard && (
                        <button
                            type="submit"
                            disabled={model.busy}
                            className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <CheckIcon />
                            Guardar aseguradora
                        </button>
                    )}
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default InsurerForm
